import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import FieldFormFields from 'components/forms/FieldFormFields';

import useField from 'hooks/useField';
import useFieldBoundaryAssignments from 'hooks/useFieldBoundaryAssignments';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props, UpdateFieldFormSchema } from './types';

import styles from './styles.module.scss';

// Boundaries arrive as props: the field details page has already fetched them for its own panel,
// so the form reads that result instead of repeating the request. The page only renders this form
// once they have loaded, which is what lets them go straight into defaultValues.
const UpdateFieldForm: FunctionComponent<Props> = ({
    boundaries,
    className = '',
    formValues,
    isClosing,
    onClose,
    primaryBoundary,
    setIsClosing,
    testId = TEST_ID
}) => {
    const { updateField } = useField();
    const { saveBoundaryAssignments } = useFieldBoundaryAssignments();
    const { SAVE } = useTranslation();

    const defaultValues: UpdateFieldFormSchema = {
        boundaries,
        client: {
            label: formValues?.clientName ?? '',
            value: formValues?.clientId ?? ''
        },
        farm: {
            label: formValues?.parcelName ?? '',
            value: formValues?.parcelId ?? ''
        },
        id: formValues?.id ?? '',
        name: formValues?.name ?? '',
        primaryBoundary
    };

    const form = useForm<UpdateFieldFormSchema>({
        defaultValues,
        mode: 'onChange'
    });

    const {
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose: () => setIsClosing?.(false),
        onClose,
        requestingClose: isClosing
    });

    const onSubmit = async (data: UpdateFieldFormSchema) => {
        if (!formValues) {
            return;
        }

        const field = await updateField({
            field: {
                name: data.name,
                parcelId: data.farm.value
            },
            id: formValues.id
        });

        if (!field) {
            return;
        }

        const isSaved = await saveBoundaryAssignments({
            boundaries: data.boundaries,
            initialBoundaries: boundaries,
            initialPrimary: primaryBoundary,
            primary: data.primaryBoundary,
            siteId: formValues.id
        });

        if (isSaved) {
            onClose?.();
        }
    };

    return (
        <VerticalContainer className={`${styles.updateFieldForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form className={styles.form} form={form}>
                    <FieldFormFields siteId={formValues?.id} />

                    <FormSubmitPanel
                        buttonText={SAVE}
                        className={styles.formSubmitPanel}
                        errors={errors}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default UpdateFieldForm;
